export type AdultingSectionPath = '/money' | '/home' | '/health' | '/family' | '/work';
export type AdultingNavigationDestinationPath = AdultingSectionPath | '/community' | '/pets';

export type AdultingSectionAccessContext = {
  workType?: string | null;
  dependantTypes?: readonly string[] | null;
  hasChildren?: boolean | null;
  partnerName?: string | null;
  hasHouseholdShare?: boolean | null;
  communityAccessStatus?: 'active' | 'suspended' | 'banned' | null;
  hasTrackedPets?: boolean | null;
};

export const ADULTING_BASE_SECTION_PATHS = ['/home', '/money', '/health'] as const satisfies readonly AdultingSectionPath[];
export const ADULTING_PINNED_SECTIONS_STORAGE_KEY = 'adulting_pinned_sections_v1';
export const ADULTING_MAX_PINNED_SECTION_COUNT = 4;
export const ADULTING_NAVIGATION_PREFERENCES_CHANGED_EVENT = 'adulting:navigation-preferences-changed';

export const ADULTING_SECTION_OPTION_LABELS: Record<AdultingSectionPath, string> = {
  '/money': '💰 Money',
  '/home': '🏠 Home',
  '/health': '🧠 Health',
  '/family': '👥 People',
  '/work': '💼 Work',
};
export const ADULTING_DESTINATION_OPTION_LABELS: Record<AdultingNavigationDestinationPath, string> = {
  ...ADULTING_SECTION_OPTION_LABELS,
  '/community': '🫶 Community',
  '/pets': '🐾 Pets',
};
export const ADULTING_DESTINATION_NAV_LABELS: Record<AdultingNavigationDestinationPath, string> = {
  '/money': 'Money',
  '/home': 'Home',
  '/health': 'Health',
  '/family': 'People',
  '/work': 'Work',
  '/community': 'Community',
  '/pets': 'Pets',
};

export function getAdultingSectionPlainLabel(path: AdultingSectionPath) {
  return ADULTING_SECTION_OPTION_LABELS[path].replace(/^[^\s]+\s/, '');
}

export function getAdultingDestinationPlainLabel(path: AdultingNavigationDestinationPath) {
  return ADULTING_DESTINATION_OPTION_LABELS[path].replace(/^[^\s]+\s/, '');
}

export function hasPeopleSection(context: AdultingSectionAccessContext) {
  const dependantCount = context.dependantTypes?.length ?? 0;
  return (
    dependantCount > 0
    || Boolean(context.hasChildren)
    || Boolean(context.partnerName?.trim())
    || Boolean(context.hasHouseholdShare)
  );
}

export function hasWorkSection(_context: AdultingSectionAccessContext) {
  return true;
}

export function getAvailableAdultingSections(context: AdultingSectionAccessContext): AdultingSectionPath[] {
  const sections: AdultingSectionPath[] = ['/home'];

  if (hasPeopleSection(context)) {
    sections.push('/family');
  }

  sections.push('/money', '/health');

  if (hasWorkSection(context)) {
    sections.push('/work');
  }

  return sections;
}

export function hasAdultingCommunityDestination(context: AdultingSectionAccessContext) {
  return context.communityAccessStatus === 'active';
}

export function hasAdultingPetsDestination(context: AdultingSectionAccessContext) {
  return Boolean(context.hasTrackedPets);
}

export function getAvailableAdultingNavigationDestinations(
  context: AdultingSectionAccessContext,
): AdultingNavigationDestinationPath[] {
  const destinations: AdultingNavigationDestinationPath[] = [...getAvailableAdultingSections(context)];

  if (hasAdultingCommunityDestination(context)) {
    destinations.push('/community');
  }

  if (hasAdultingPetsDestination(context)) {
    destinations.push('/pets');
  }

  return destinations;
}

export function getDefaultPinnedAdultingSections(context: AdultingSectionAccessContext) {
  return getAvailableAdultingSections(context).slice(0, ADULTING_MAX_PINNED_SECTION_COUNT);
}

export function getDefaultPinnedAdultingDestinations(context: AdultingSectionAccessContext) {
  return getAvailableAdultingNavigationDestinations(context).slice(0, ADULTING_MAX_PINNED_SECTION_COUNT);
}

export function isAdultingSectionPath(value: string | null | undefined): value is AdultingSectionPath {
  return value === '/money' || value === '/home' || value === '/health' || value === '/family' || value === '/work';
}

export function isAdultingNavigationDestinationPath(
  value: string | null | undefined,
): value is AdultingNavigationDestinationPath {
  return isAdultingSectionPath(value) || value === '/community' || value === '/pets';
}

export function resolveAdultingDestinationPriority(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
}) {
  const available = getAvailableAdultingNavigationDestinations(input.context);
  const preferred = (input.preferred || []).filter(isAdultingNavigationDestinationPath);
  const uniquePreferred = Array.from(new Set(preferred)).filter((path) => available.includes(path));

  return [
    ...uniquePreferred,
    ...available.filter((path) => !uniquePreferred.includes(path)),
  ];
}

function movePriorityItem<T>(items: readonly T[], item: T, targetIndex: number) {
  const currentIndex = items.indexOf(item);
  if (currentIndex < 0) {
    return [...items];
  }

  const next = [...items];
  const [moved] = next.splice(currentIndex, 1);
  const boundedIndex = Math.max(0, Math.min(targetIndex, next.length));
  next.splice(boundedIndex, 0, moved);
  return next;
}

export function pinAdultingDestination(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
  destination: AdultingNavigationDestinationPath;
}) {
  const priority = resolveAdultingDestinationPriority(input);
  const destinationIndex = priority.indexOf(input.destination);
  if (destinationIndex < 0) {
    return priority;
  }

  if (destinationIndex < ADULTING_MAX_PINNED_SECTION_COUNT) {
    return priority;
  }

  return movePriorityItem(
    priority,
    input.destination,
    Math.min(ADULTING_MAX_PINNED_SECTION_COUNT - 1, priority.length - 1),
  );
}

export function keepAdultingDestinationUnderMore(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
  destination: AdultingNavigationDestinationPath;
}) {
  const priority = resolveAdultingDestinationPriority(input);
  const destinationIndex = priority.indexOf(input.destination);
  if (destinationIndex < 0 || destinationIndex >= ADULTING_MAX_PINNED_SECTION_COUNT || priority.length <= ADULTING_MAX_PINNED_SECTION_COUNT) {
    return priority;
  }

  const next = [...priority];
  const [moved] = next.splice(destinationIndex, 1);
  next.splice(Math.min(ADULTING_MAX_PINNED_SECTION_COUNT, next.length), 0, moved);
  return next;
}

export function moveAdultingDestinationEarlier(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
  destination: AdultingNavigationDestinationPath;
}) {
  const priority = resolveAdultingDestinationPriority(input);
  const destinationIndex = priority.indexOf(input.destination);
  if (destinationIndex <= 0) {
    return priority;
  }

  return movePriorityItem(priority, input.destination, destinationIndex - 1);
}

export function moveAdultingDestinationLater(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
  destination: AdultingNavigationDestinationPath;
}) {
  const priority = resolveAdultingDestinationPriority(input);
  const destinationIndex = priority.indexOf(input.destination);
  if (destinationIndex < 0 || destinationIndex >= priority.length - 1) {
    return priority;
  }

  return movePriorityItem(priority, input.destination, destinationIndex + 1);
}

export function resolveAdultingPinnedDestinations(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
}) {
  return resolveAdultingDestinationPriority(input).slice(0, ADULTING_MAX_PINNED_SECTION_COUNT);
}

export function getAdultingOverflowDestinations(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
}) {
  return resolveAdultingDestinationPriority(input).slice(ADULTING_MAX_PINNED_SECTION_COUNT);
}

export function resolveAdultingPinnedSections(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
}) {
  return resolveAdultingPinnedDestinations(input).filter(isAdultingSectionPath);
}

export function getAdultingOverflowSections(input: {
  preferred?: readonly string[] | null;
  context: AdultingSectionAccessContext;
}) {
  return getAdultingOverflowDestinations(input).filter(isAdultingSectionPath);
}

export function resolveAdultingSectionPath(input: {
  preferred?: string | null;
  lastVisited?: string | null;
  context: AdultingSectionAccessContext;
}) {
  const available = getAvailableAdultingSections(input.context);

  if (isAdultingSectionPath(input.preferred) && available.includes(input.preferred)) {
    return input.preferred;
  }

  if (isAdultingSectionPath(input.lastVisited) && available.includes(input.lastVisited)) {
    return input.lastVisited;
  }

  return available[0] || ('/home' satisfies AdultingSectionPath);
}
